/**
 * ReversePreviewPlayer — real-time reverse video preview.
 *
 * Composes on top of TimbricaPlayer: the shared video player owns the DOM
 * (timeline, trim handles, play button, fullscreen). We add a canvas
 * overlay that paints the MIRROR frame of video.currentTime within the
 * trim range. The <video> plays muted + invisible; canvas shows reversed
 * content at the same timeline position.
 *
 * Pipeline:
 *   File → mp4box (MP4 demux) → EncodedVideoChunk → VideoDecoder → VideoFrame
 *        → OffscreenCanvas downscale → ImageBitmap ring buffer
 *
 * Playback:
 *   RAF loop: frame = nearest(trimStart + trimEnd − video.currentTime)
 *
 * Fallback (WebCodecs unavailable / non-MP4 container / codec unsupported):
 *   Plain TimbricaPlayer plays the original with a "preview unavailable" badge.
 *
 * Usage:
 *   const rp = new ReversePreviewPlayer(containerEl, {
 *       maxHeight: 400,
 *       onReady:       fn(rp),
 *       onTrimChange:  fn(s, e),
 *       onFallback:    fn(reason),
 *       labels: { generating, unavailable, truncated, viewOriginal, viewReverse }
 *   });
 *   rp.load(file);
 *   rp.getTrimRange();   // {start, end}
 *   rp.destroy();
 */
(function(){
'use strict';

/* ═══════════════════════════════════════════
   Tuning
   ═══════════════════════════════════════════ */
var MP4BOX_URL      = '/vendor/mp4box/mp4box.all.min.js';
var MAX_FRAMES      = 450;
var MAX_WIDTH       = 960;
var MAX_HEIGHT      = 540;
var MAX_PREVIEW_SEC = 30;

/* ═══════════════════════════════════════════
   CSS
   ═══════════════════════════════════════════ */
var cssInjected = false;
var CSS = `
.rp-canvas-overlay{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;background:#000;z-index:1;display:none;pointer-events:none}
.rp-video-hidden{opacity:0!important}
.rp-badge{position:absolute;top:10px;left:10px;display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:99px;background:rgba(0,0,0,.55);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);color:#fff;font-size:12px;font-weight:600;z-index:10;font-family:system-ui,sans-serif;pointer-events:none;line-height:1.2}
.rp-badge-warn{background:rgba(245,158,11,.92)}
.rp-mode{position:absolute;top:10px;right:10px;display:flex;border-radius:10px;overflow:hidden;background:rgba(0,0,0,.55);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);z-index:10;font-family:system-ui,sans-serif}
.rp-mode-btn{padding:6px 12px;background:none;border:none;color:rgba(255,255,255,.7);font-size:12px;font-weight:600;cursor:pointer;transition:all .15s;font-family:inherit;line-height:1}
.rp-mode-btn:hover{color:#fff;background:rgba(255,255,255,.1)}
.rp-mode-btn.active{color:#000;background:#fff}
.rp-status{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;background:rgba(0,0,0,.55);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);z-index:6;gap:10px;padding:16px;text-align:center;font-family:system-ui,sans-serif}
.rp-status-hidden{display:none}
.rp-spinner{width:32px;height:32px;border:3px solid rgba(255,255,255,.2);border-top-color:#fff;border-radius:50%;animation:rp-spin .9s linear infinite}
@keyframes rp-spin{to{transform:rotate(360deg)}}
.rp-status-text{font-size:13px;color:rgba(255,255,255,.9);max-width:80%;line-height:1.4}
.rp-status-prog{font-size:12px;color:rgba(255,255,255,.65);font-variant-numeric:tabular-nums}
`;
function injectCss(){
    if(cssInjected) return;
    cssInjected = true;
    var s = document.createElement('style');
    s.textContent = CSS;
    document.head.appendChild(s);
}

/* ═══════════════════════════════════════════
   Feature detection & loaders
   ═══════════════════════════════════════════ */
function webcodecsSupported(){
    return typeof VideoDecoder !== 'undefined'
        && typeof EncodedVideoChunk !== 'undefined'
        && typeof OffscreenCanvas !== 'undefined'
        && typeof createImageBitmap === 'function';
}

function isMp4Container(file){
    return file.type === 'video/mp4'
        || file.type === 'video/quicktime'
        || /\.(mp4|m4v|mov)$/i.test(file.name);
}

/* Remux any FFmpeg-readable container to MP4 with stream copy (no re-encode).
   Seconds for 100MB: literally copies bytes into a new box structure.
   Depends on the global FFmpegProcessor (loaded by every video tool anyway). */
function remuxToMp4(file, ctrl, onProgress){
    if(typeof FFmpegProcessor === 'undefined'){
        return Promise.reject(new Error('ffmpeg_not_loaded'));
    }
    return FFmpegProcessor.init().then(function(ok){
        if(!ok) throw new Error('ffmpeg_init_failed');
        if(ctrl.aborted) throw new Error('aborted');
        if(onProgress){
            try { FFmpegProcessor.onProgress(function(r){ onProgress(r); }); } catch(_){}
        }
        return FFmpegProcessor.process(
            file,
            ['-c', 'copy', '-movflags', '+faststart'],
            'remuxed.mp4'
        );
    }).then(function(res){
        if(!res || !res.blob || !res.blob.size) throw new Error('remux_empty');
        // Wrap blob as a File so downstream mp4box/extractor sees file.name/.stream()
        var mp4 = new File([res.blob], (file.name || 'input').replace(/\.[^.]+$/, '') + '.mp4', {
            type: 'video/mp4',
            lastModified: Date.now(),
        });
        return mp4;
    });
}

var _mp4boxPromise = null;
function loadMp4Box(){
    if(_mp4boxPromise) return _mp4boxPromise;
    _mp4boxPromise = new Promise(function(resolve, reject){
        if(window.MP4Box && window.DataStream) return resolve();
        var s = document.createElement('script');
        s.src = MP4BOX_URL;
        s.onload = function(){ resolve(); };
        s.onerror = function(){ _mp4boxPromise = null; reject(new Error('mp4box load failed')); };
        document.head.appendChild(s);
    });
    return _mp4boxPromise;
}

/* Extract codec-specific config box (avcC / hvcC / vpcC / av1C) */
function codecDescription(trak){
    if(!trak || !trak.mdia || !trak.mdia.minf || !trak.mdia.minf.stbl) return null;
    var entries = trak.mdia.minf.stbl.stsd.entries;
    for(var i = 0; i < entries.length; i++){
        var e = entries[i];
        var box = e.avcC || e.hvcC || e.vpcC || e.av1C;
        if(box){
            var ds = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
            box.write(ds);
            return new Uint8Array(ds.buffer, 8); // strip 8-byte box header
        }
    }
    return null;
}

/* ═══════════════════════════════════════════
   Frame extractor
   ═══════════════════════════════════════════ */
function extractFrames(file, ctrl, onProgress){
    return new Promise(function(resolve, reject){
        loadMp4Box().then(run, reject);

        function run(){
            var mp4box = MP4Box.createFile();
            var videoTrack = null, decoder = null;
            var frames = [];
            var durationSec = 0, srcW = 0, srcH = 0, outW = 0, outH = 0;
            var srcCodedW = 0, srcCodedH = 0, srcRot = 0;
            var offCanvas = null, offCtx = null;
            var targetFps = 30, targetIntervalMs = 1000 / 30;
            var lastKeptMs = -Infinity, keepStartMs = 0;
            var flushed = false, finalized = false;
            var samplesFed = 0, decodedFrames = 0;
            var estTotal = 0;

            function abort(err){
                if(finalized) return;
                finalized = true;
                try{ decoder && decoder.state !== 'closed' && decoder.close(); }catch(_){}
                frames.forEach(function(f){ try{ f.bmp.close(); }catch(_){} });
                reject(err);
            }

            function finalize(){
                if(finalized) return;
                finalized = true;
                try{ decoder && decoder.state !== 'closed' && decoder.close(); }catch(_){}
                resolve({
                    frames: frames,
                    fps: targetFps,
                    width: outW,
                    height: outH,
                    duration: durationSec,
                    truncated: durationSec > MAX_PREVIEW_SEC,
                    previewDuration: Math.min(durationSec, MAX_PREVIEW_SEC),
                });
            }

            mp4box.onError = function(err){ abort(new Error('mp4box: ' + err)); };

            // Stream file bytes into mp4box; onReady fires when moov is parsed.
            streamFile();

            mp4box.onReady = function(info){
                if(ctrl.aborted) return abort(new Error('aborted'));
                var vts = info.videoTracks;
                if(!vts || !vts.length) return abort(new Error('no_video_track'));
                videoTrack = vts[0];

                durationSec = videoTrack.duration / videoTrack.timescale;
                /* Кадры VideoDecoder приходят в coded-координатах, а
                   reverse-webcodecs.js (тот самый экспорт, который это превью
                   показывает) поворот применяет. Пока здесь стоял coded-размер,
                   превью и результат расходились на съёмке с телефона: одно
                   боком, другое стоймя. */
                srcCodedW = videoTrack.video.width;
                srcCodedH = videoTrack.video.height;
                srcRot = window.VideoOrientation ? window.VideoOrientation.fromTkhd(mp4box, videoTrack.id) : 0;
                var _disp = window.VideoOrientation
                    ? window.VideoOrientation.display(srcCodedW, srcCodedH, srcRot)
                    : { w: srcCodedW, h: srcCodedH };
                srcW = _disp.w;
                srcH = _disp.h;

                var scale = Math.min(1, MAX_WIDTH / srcW, MAX_HEIGHT / srcH);
                outW = Math.max(2, Math.floor(srcW * scale / 2) * 2);
                outH = Math.max(2, Math.floor(srcH * scale / 2) * 2);
                try {
                    offCanvas = new OffscreenCanvas(outW, outH);
                    offCtx = offCanvas.getContext('2d', { alpha: false });
                } catch(e){ return abort(new Error('offscreen_canvas')); }

                var sourceFps = videoTrack.nb_samples / durationSec;
                if(!isFinite(sourceFps) || sourceFps <= 0) sourceFps = 30;
                var previewDur = Math.min(durationSec, MAX_PREVIEW_SEC);
                targetFps = Math.min(30, sourceFps);
                if(targetFps * previewDur > MAX_FRAMES) targetFps = MAX_FRAMES / previewDur;
                targetIntervalMs = 1000 / targetFps;
                estTotal = Math.ceil(targetFps * previewDur);
                keepStartMs = Math.max(0, (durationSec - MAX_PREVIEW_SEC) * 1000);

                var trak = mp4box.getTrackById(videoTrack.id);
                var description = codecDescription(trak);
                var codec = videoTrack.codec;

                decoder = new VideoDecoder({
                    output: function(frame){
                        if(ctrl.aborted || finalized){ try{ frame.close(); }catch(_){} return; }
                        var tsMs = frame.timestamp / 1000;
                        if(tsMs < keepStartMs){
                            try{ frame.close(); }catch(_){}
                            decodedFrames++;
                            maybeFinalize();
                            return;
                        }
                        if(tsMs - lastKeptMs >= targetIntervalMs - 0.5){
                            try {
                                /* apply() на Chromium закрывает исходный кадр и отдаёт
                                   обёртку — закрывать дальше надо ЕЁ, иначе на каждом
                                   кадре повёрнутого клипа обёртка жила до сборщика. */
                                if (window.VideoOrientation) { frame = window.VideoOrientation.apply(frame, srcRot); window.VideoOrientation.draw(offCtx, frame, 0, 0, outW, outH); }
                                else offCtx.drawImage(frame, 0, 0, outW, outH);
                                createImageBitmap(offCanvas).then(function(bmp){
                                    if(ctrl.aborted || finalized){ try{ bmp.close(); }catch(_){} return; }
                                    if(frames.length < MAX_FRAMES + 8){
                                        frames.push({ bmp: bmp, ts: tsMs });
                                        lastKeptMs = tsMs;
                                    } else { try{ bmp.close(); }catch(_){} }
                                }).catch(function(){});
                            } catch(_){}
                        }
                        try{ frame.close(); }catch(_){}
                        decodedFrames++;
                        if(onProgress) onProgress(Math.min(1, decodedFrames / Math.max(1, samplesFed)));
                        maybeFinalize();
                    },
                    error: function(e){ abort(new Error('decoder: ' + (e.message || e))); }
                });

                var config = { codec: codec, codedWidth: srcCodedW, codedHeight: srcCodedH, hardwareAcceleration: 'prefer-hardware' };
                if(description) config.description = description;

                VideoDecoder.isConfigSupported(config).then(function(sup){
                    if(!sup.supported){
                        var cfg2 = Object.assign({}, config); delete cfg2.hardwareAcceleration;
                        return VideoDecoder.isConfigSupported(cfg2).then(function(sup2){
                            if(!sup2.supported) throw new Error('codec_unsupported');
                            decoder.configure(cfg2);
                            mp4box.setExtractionOptions(videoTrack.id, null, { nbSamples: 50 });
                            mp4box.start();
                        });
                    }
                    decoder.configure(config);
                    mp4box.setExtractionOptions(videoTrack.id, null, { nbSamples: 50 });
                    mp4box.start();
                }).catch(abort);
            };

            mp4box.onSamples = function(id, user, samples){
                if(ctrl.aborted || finalized) return;
                for(var i = 0; i < samples.length; i++){
                    var s = samples[i];
                    try {
                        decoder.decode(new EncodedVideoChunk({
                            type: s.is_sync ? 'key' : 'delta',
                            timestamp: (s.cts * 1e6) / s.timescale,
                            duration: (s.duration * 1e6) / s.timescale,
                            data: s.data,
                        }));
                        samplesFed++;
                    } catch(_){}
                }
            };

            function streamFile(){
                var offset = 0;
                var reader = file.stream().getReader();
                function pump(){
                    if(ctrl.aborted || finalized) return;
                    reader.read().then(function(r){
                        if(r.done){
                            mp4box.flush();
                            flushed = true;
                            if(decoder){
                                decoder.flush().then(maybeFinalize).catch(maybeFinalize);
                            }
                            return;
                        }
                        var chunk = r.value;
                        var buf = (chunk.byteOffset === 0 && chunk.byteLength === chunk.buffer.byteLength)
                            ? chunk.buffer
                            : chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);
                        buf.fileStart = offset;
                        offset += chunk.byteLength;
                        try { mp4box.appendBuffer(buf); } catch(e){ return abort(e); }
                        pump();
                    }).catch(abort);
                }
                pump();
            }

            function maybeFinalize(){
                if(!flushed || !decoder) return;
                if(decoder.decodeQueueSize > 0) return;
                setTimeout(finalize, 20);
            }
        }
    });
}

/* ═══════════════════════════════════════════
   Player — composes TimbricaPlayer + canvas overlay
   ═══════════════════════════════════════════ */
function ReversePreviewPlayer(container, opts){
    if(!container) throw new Error('ReversePreviewPlayer: container required');
    if(typeof TimbricaPlayer === 'undefined') throw new Error('TimbricaPlayer not loaded');
    opts = opts || {};
    injectCss();

    var self = this;
    this.opts = opts;
    this.labels = Object.assign({
        remuxing:     'Converting container…',
        generating:   'Generating reverse preview…',
        unavailable:  'Reverse preview unavailable in this browser',
        truncated:    'Preview shows first :sec s of reversed result',
        viewOriginal: 'Original',
        viewReverse:  'Reverse',
    }, opts.labels || {});

    // State
    this.file        = null;
    this.mode        = 'reverse';        // 'reverse' | 'original'
    this.frames      = null;             // [{bmp, ts}] in source ms
    this.truncated   = false;
    this.fallback    = false;
    this._rafId      = null;
    this._abortCtrl  = { aborted: false };

    // Create the shared player first — owns timeline, trim, play, fullscreen.
    this.tp = new TimbricaPlayer(container, {
        trim: true,
        volume: false,
        speed: false,
        fullscreen: true,
        maxHeight: opts.maxHeight || 400,
        onReady: function(p){
            if(opts.onReady) opts.onReady(self);
        },
        onTrimChange: function(s, e){
            if(opts.onTrimChange) opts.onTrimChange(s, e);
        },
    });

    // Overlay DOM on top of TimbricaPlayer's wrap (.tp-wrap is position:relative)
    var wrap = this.tp.container;
    this.wrap = wrap;

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'rp-canvas-overlay';
    wrap.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d', { alpha: false });

    this.badge = document.createElement('div');
    this.badge.className = 'rp-badge';
    this.badge.innerHTML = '<span class="rp-badge-text"></span>';
    this.badgeText = this.badge.querySelector('.rp-badge-text');
    wrap.appendChild(this.badge);

    this.modeBar = document.createElement('div');
    this.modeBar.className = 'rp-mode';
    this.modeBar.innerHTML =
        '<button class="rp-mode-btn active" data-mode="reverse"></button>' +
        '<button class="rp-mode-btn" data-mode="original"></button>';
    this.modeBar.style.display = 'none';
    this.modeBar.children[0].textContent = this.labels.viewReverse;
    this.modeBar.children[1].textContent = this.labels.viewOriginal;
    wrap.appendChild(this.modeBar);
    this.modeBar.addEventListener('click', function(e){
        var btn = e.target.closest('.rp-mode-btn');
        if(btn) self.setMode(btn.dataset.mode);
    });

    this.status = document.createElement('div');
    this.status.className = 'rp-status rp-status-hidden';
    this.status.innerHTML = '<div class="rp-spinner"></div><div class="rp-status-text"></div><div class="rp-status-prog"></div>';
    this.statusText = this.status.querySelector('.rp-status-text');
    this.statusProg = this.status.querySelector('.rp-status-prog');
    wrap.appendChild(this.status);
}

ReversePreviewPlayer.prototype._setStatus = function(text, show, prog){
    if(show){
        this.status.classList.remove('rp-status-hidden');
        this.statusText.textContent = text || '';
        this.statusProg.textContent = prog || '';
    } else {
        this.status.classList.add('rp-status-hidden');
    }
};

ReversePreviewPlayer.prototype.load = function(file){
    var self = this;
    this.file = file;

    // Always load into TimbricaPlayer (drives timeline + trim). In fallback it's visible;
    // in reverse mode it stays invisible but still plays to drive the timeline/currentTime.
    this.tp.load(file);

    if(!webcodecsSupported()){ return this._useFallback('no_webcodecs'); }

    // Decide pipeline:
    //   MP4 / MOV → feed directly to mp4box + WebCodecs
    //   Anything else (MKV, WebM, AVI, FLV, …) → FFmpeg-WASM remux -c copy → MP4 → same pipeline
    // Remux copies codec streams into MP4 container (no re-encode): seconds for 100MB.
    var prep;
    if(isMp4Container(file)){
        prep = Promise.resolve(file);
    } else {
        this._setStatus(this.labels.remuxing, true, '');
        prep = remuxToMp4(file, this._abortCtrl, function(r){
            self.statusProg.textContent = Math.round(r * 100) + '%';
        });
    }

    prep.then(function(mp4){
        if(self._abortCtrl.aborted) return;
        self._setStatus(self.labels.generating, true, '0%');
        return extractFrames(mp4, self._abortCtrl, function(p){
            self.statusProg.textContent = Math.round(p * 100) + '%';
        });
    }).then(function(res){
        if(!res) return;
        if(self._abortCtrl.aborted) return;
        if(!res.frames.length) return self._useFallback('no_frames');

        self.frames = res.frames;
        self.truncated = res.truncated;
        self.canvas.width = res.width;
        self.canvas.height = res.height;

        self.badge.classList.toggle('rp-badge-warn', res.truncated);
        if(res.truncated){
            self.badgeText.textContent = self.labels.truncated.replace(':sec', Math.round(res.previewDuration));
        } else {
            self.badgeText.textContent = '⏪ ' + self.labels.viewReverse;
        }

        self.modeBar.style.display = 'flex';
        self._setStatus('', false);
        self.setMode('reverse');
        // Paint first reversed frame (= last original frame)
        var last = res.frames[res.frames.length - 1];
        if(last) try{ self.ctx.drawImage(last.bmp, 0, 0); }catch(_){}
    }).catch(function(err){
        console.warn('[reverse-preview] extraction failed:', err && err.message);
        self._useFallback(err && err.message || 'extract_failed');
    });
};

ReversePreviewPlayer.prototype._useFallback = function(reason){
    if(this.fallback) return;
    this.fallback = true;
    this._abortCtrl.aborted = true;

    this._setStatus('', false);
    this.canvas.style.display = 'none';
    this.modeBar.style.display = 'none';
    this.tp.video.classList.remove('rp-video-hidden');
    try{ this.tp.video.muted = false; }catch(_){}

    this.badge.classList.add('rp-badge-warn');
    this.badgeText.textContent = this.labels.unavailable;

    if(this.opts.onFallback) this.opts.onFallback(reason);
};

ReversePreviewPlayer.prototype.setMode = function(mode){
    if(this.fallback || !this.frames) return;
    if(mode !== 'reverse' && mode !== 'original') return;
    this.mode = mode;

    this.modeBar.children[0].classList.toggle('active', mode === 'reverse');
    this.modeBar.children[1].classList.toggle('active', mode === 'original');

    if(mode === 'reverse'){
        this.tp.video.classList.add('rp-video-hidden');
        this.tp.video.muted = true;
        this.canvas.style.display = 'block';
        this.badgeText.textContent = this.truncated
            ? this.labels.truncated.replace(':sec', Math.round(Math.min(this.tp.duration, 30)))
            : '⏪ ' + this.labels.viewReverse;
        this._startRenderLoop();
    } else {
        this.tp.video.classList.remove('rp-video-hidden');
        this.tp.video.muted = false;
        this.canvas.style.display = 'none';
        this.badge.classList.remove('rp-badge-warn');
        this.badgeText.textContent = this.labels.viewOriginal;
        this._stopRenderLoop();
    }
};

ReversePreviewPlayer.prototype._startRenderLoop = function(){
    var self = this;
    if(this._rafId) return;
    function tick(){
        if(self.mode !== 'reverse' || !self.frames){ self._rafId = null; return; }
        var tr = self.tp.getTrimRange();
        var cur = self.tp.currentTime;
        if(cur < tr.start) cur = tr.start;
        if(cur > tr.end)   cur = tr.end;
        // Mirror within [trim.start, trim.end]
        var revSec = tr.start + tr.end - cur;
        var revMs = revSec * 1000;
        var frame = self._nearestFrame(revMs);
        if(frame){
            try{ self.ctx.drawImage(frame.bmp, 0, 0); }catch(_){}
        }
        self._rafId = requestAnimationFrame(tick);
    }
    this._rafId = requestAnimationFrame(tick);
};

ReversePreviewPlayer.prototype._stopRenderLoop = function(){
    if(this._rafId){ cancelAnimationFrame(this._rafId); this._rafId = null; }
};

/* Binary search on frames[] (ascending ts) — O(log n). */
ReversePreviewPlayer.prototype._nearestFrame = function(ms){
    var f = this.frames;
    if(!f || !f.length) return null;
    var lo = 0, hi = f.length - 1;
    if(ms <= f[0].ts)  return f[0];
    if(ms >= f[hi].ts) return f[hi];
    while(lo <= hi){
        var mid = (lo + hi) >> 1;
        if(f[mid].ts < ms) lo = mid + 1;
        else hi = mid - 1;
    }
    // lo points to first frame with ts ≥ ms; pick closest of lo / lo-1
    var a = f[lo - 1], b = f[lo];
    if(!a) return b;
    if(!b) return a;
    return (ms - a.ts) < (b.ts - ms) ? a : b;
};

ReversePreviewPlayer.prototype.getTrimRange = function(){ return this.tp.getTrimRange(); };
ReversePreviewPlayer.prototype.setTrimRange = function(s, e){ return this.tp.setTrimRange(s, e); };

Object.defineProperty(ReversePreviewPlayer.prototype, 'duration', {
    get: function(){ return this.tp.duration; }
});
Object.defineProperty(ReversePreviewPlayer.prototype, 'video', {
    get: function(){ return this.tp.video; }
});

ReversePreviewPlayer.prototype.destroy = function(){
    this._stopRenderLoop();
    this._abortCtrl.aborted = true;
    if(this.frames){
        this.frames.forEach(function(f){ try{ f.bmp.close(); }catch(_){} });
        this.frames = null;
    }
    try{ this.tp.destroy(); }catch(_){}
};

ReversePreviewPlayer.supported = webcodecsSupported;

window.ReversePreviewPlayer = ReversePreviewPlayer;

})();
